import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, X, Plus } from "lucide-react";
import { coachDashApi, DASH_TESTIDS as D } from "@/lib/coachDashApi";

/**
 * Coach profile editor — headline, bio, specialties, and pricing.
 *
 * Kept intentionally scoped: anything that affects rating, verified badges,
 * approval status, or Stripe wiring stays admin-only on the backend. This
 * component only exposes fields the coach can safely self-manage.
 */
export default function ProfileEditor({ coachEmail }) {
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSpecialty, setNewSpecialty] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await coachDashApi.getProfile(coachEmail);
      setInitial(p);
      setForm(p);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load your profile.");
    } finally {
      setLoading(false);
    }
  }, [coachEmail]);

  useEffect(() => { load(); }, [load]);

  if (loading || !form) {
    return (
      <section data-testid={D.profileSection} className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </section>
    );
  }

  const dirty =
    form.headline !== initial.headline ||
    form.bio !== initial.bio ||
    Number(form.price_per_session) !== Number(initial.price_per_session) ||
    JSON.stringify(form.specialties) !== JSON.stringify(initial.specialties);

  const addSpecialty = () => {
    const v = newSpecialty.trim();
    if (!v) return;
    if (form.specialties.length >= 8) return toast.error("Max 8 specialties.");
    if (form.specialties.some((s) => s.toLowerCase() === v.toLowerCase())) {
      return toast.error("Already added.");
    }
    setForm({ ...form, specialties: [...form.specialties, v.slice(0, 32)] });
    setNewSpecialty("");
  };
  const removeSpecialty = (i) => {
    setForm({ ...form, specialties: form.specialties.filter((_, idx) => idx !== i) });
  };

  const save = async () => {
    if (form.headline.trim().length < 4) return toast.error("Headline is too short.");
    if (form.bio.trim().length < 20) return toast.error("Bio must be at least 20 characters.");
    if (form.specialties.length === 0) return toast.error("Add at least one specialty.");
    const price = Number(form.price_per_session);
    if (!Number.isFinite(price) || price < 1) return toast.error("Enter a valid price.");
    setSaving(true);
    try {
      const p = await coachDashApi.saveProfile(coachEmail, {
        headline: form.headline.trim(),
        bio: form.bio.trim(),
        specialties: form.specialties,
        price_per_session: price,
        hourly_rate: price, // keep in sync
      });
      setInitial(p);
      setForm(p);
      toast.success("Profile saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid={D.profileSection} className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl md:text-2xl tracking-tight text-slate-900">Profile</h2>
          <p className="mt-1 text-[13.5px] text-slate-500 max-w-[52ch]">
            What clients see on your public page. Changes go live instantly.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid={D.profileSave}
          className="btn-primary h-10 px-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>

      <div className="mt-6 space-y-6">
        <Field label="Headline" hint="A short one-liner that describes what makes you different.">
          <input
            data-testid={D.profileHeadline}
            value={form.headline}
            onChange={(e) => setForm({ ...form, headline: e.target.value.slice(0, 160) })}
            maxLength={160}
            placeholder="Former pro boxer • builds real, tested confidence"
            className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3.5 text-[14px] focus:outline-none focus:border-slate-400"
          />
          <div className="mt-1 text-[11.5px] text-slate-400 text-right">{form.headline.length}/160</div>
        </Field>

        <Field label="Bio" hint="A short paragraph on your background, style, and who you love working with.">
          <textarea
            data-testid={D.profileBio}
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 2000) })}
            rows={6}
            maxLength={2000}
            placeholder="Ten years in the ring, six years coaching everyone from first-timers to amateur competitors…"
            className="w-full rounded-xl border border-slate-200 bg-white p-3.5 text-[14px] focus:outline-none focus:border-slate-400 resize-none"
          />
          <div className="mt-1 text-[11.5px] text-slate-400 text-right">{form.bio.length}/2000</div>
        </Field>

        <Field label="Specialties" hint="Up to 8 short tags. Boxing, Nutrition, Recovery…">
          <div data-testid={D.profileSpecialties} className="flex flex-wrap gap-1.5">
            {form.specialties.map((s, i) => (
              <span
                key={`${s}-${i}`}
                className="inline-flex items-center gap-1 h-8 pl-3 pr-2 rounded-full bg-slate-100 text-slate-800 text-[12.5px] font-medium"
              >
                {s}
                <button
                  type="button"
                  onClick={() => removeSpecialty(i)}
                  aria-label={`Remove ${s}`}
                  className="w-5 h-5 rounded-full hover:bg-slate-200 inline-flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              value={newSpecialty}
              onChange={(e) => setNewSpecialty(e.target.value.slice(0, 32))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSpecialty(); } }}
              placeholder="Add a specialty"
              maxLength={32}
              className="flex-1 h-10 rounded-xl border border-slate-200 bg-white px-3 text-[13.5px] focus:outline-none focus:border-slate-400"
            />
            <button
              type="button"
              onClick={addSpecialty}
              className="h-10 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-[13px] font-semibold inline-flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </Field>

        <Field label="Price per session (USD)" hint="What you charge for one session. Payouts and Stripe fees are calculated from this.">
          <div className="flex items-center gap-2">
            <span className="text-slate-500 text-[15px]">$</span>
            <input
              data-testid={D.profilePrice}
              type="number"
              min={1}
              max={10000}
              value={form.price_per_session}
              onChange={(e) => setForm({ ...form, price_per_session: e.target.value })}
              className="w-32 h-11 rounded-xl border border-slate-200 bg-white px-3.5 text-[14px] focus:outline-none focus:border-slate-400"
            />
            <span className="text-slate-500 text-[13px]">per session</span>
          </div>
        </Field>
      </div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[12px] tracking-[0.22em] uppercase text-slate-500 font-semibold">{label}</label>
      {hint && <div className="mt-1 text-[12.5px] text-slate-500 max-w-[64ch]">{hint}</div>}
      <div className="mt-2">{children}</div>
    </div>
  );
}
